import Clipboard from "../../edit/Clipboard";
import LabelCollection from "../../shapes/labels/LabelCollection";
import AbstractShape from "../../shapes/shapes/AbstractShape";
import RotateCoordinate from "../../shapes/support/RotateCoordinate";
import { focusInput } from "../../shared/utils";
import AbstractTool from "../AbstractTool";
import SelectionMenu from "../SelectionMenu";
import { BRING_FORWARD, BRING_TO_FRONT, LABELS, MOVE, MOVE_WITH_ARROW_KEY, RESHAPE, ROTATE, SEND_BACKWARD, SEND_TO_BACK } from "../actions/ActionHelper";

export default class SelectTool extends AbstractTool {
    dragging = false;
    selectedElement = null;
    scrollEnabled = true;
    menu = null;
    prevX = null;
    prevY = null;
    menu = new SelectionMenu(this.provider, 'edit');
    resetPreviousTool = true;
    click = null;
    action = null;
    multiSelectMode = false;

    activate() {
        super.activate();
        if (this.touch) {
            this.multiSelectMode = false;
            this.provider.menu.showMultiSelectButton((active) => {
                this.multiSelectMode = active;
            });
        }
    }

    display() {
        if (this.dragging && this.click) {
            this.p.rectMode(this.p.CORNERS);
            this.p.stroke(0);
            this.p.noFill();
            this.p.strokeWeight(1);
            let lineLength = 5;
            let minX = Math.min(this.click.currentX, this.view.currentX);
            let minY = Math.min(this.click.currentY, this.view.currentY);
            let maxX = Math.max(this.click.currentX, this.view.currentX);
            let maxY = Math.max(this.click.currentY, this.view.currentY);
            for (let i = minX; i <= maxX; i += lineLength * 2) {
                this.p.line(i, minY, Math.min(i + lineLength, maxX), minY);
            }
            for (let i = minX; i <= maxX; i += lineLength * 2) {
                this.p.line(i, maxY, Math.min(i + lineLength, maxX), maxY);
            }
            for (let i = minY; i <= maxY; i += lineLength * 2) {
                this.p.line(minX, i, minX, Math.min(i + lineLength, maxY));
            }
            for (let i = minY; i <= maxY; i += lineLength * 2) {
                this.p.line(maxX, i, maxX, Math.min(i + lineLength, maxY));
            }
            if (this.p.keyIsDown(this.p.CONTROL)) {
                this.p.fill('#40dd70')
                this.p.stroke('#000000');
                this.p.textSize(20);
                this.p.text('+', this.view.currentX + 10, this.view.currentY);
            } else if (this.p.keyIsDown(this.p.SHIFT)) {
                this.p.fill('#ee3000')
                this.p.stroke('#000000');
                this.p.textSize(20);
                this.p.text('-', this.view.currentX + 10, this.view.currentY);
            }
        }
    }

    // Order of actions:
    // 1. pressMouseOnCanvas
    // 2. dragMouse
    // 3. releaseMouseOnCanvas
    // 4. clickMouseOnCanvas
    pressMouseOnCanvas() {
        if (this.p.mouseButton === this.p.RIGHT) {
            let hovered = this.elements.hoveredAll();
            if (hovered.length <= 1) {
                this.clickMouseOnCanvas();
                return;
            }
            this.hoverSet.reset();
            this.menu.refresh(hovered);
            this.dontDrag = true;
            return;
        }
        this.selectedElement = this.selection.hovered();
        if (!this.selectedElement && !this.menu.initialized) {
            this.click = this.view.snappedCoordinate;
        }
    }

    dragMouse(event) {
        if (this.dontDrag) {
            return;
        }
        this.dragging = true;
        if (this.action) {
            this.action.update();
        } else if (this.selectedElement) {
            this.cursor.grabbing();
            if (this.selectedElement instanceof AbstractShape) {
                this.action = this.actions.new(MOVE, this.selectedElement, this.selection.collection);
            } else if (this.selectedElement instanceof RotateCoordinate) {
                this.action = this.actions.new(ROTATE, this.selectedElement);
            } else if (this.selectedElement instanceof LabelCollection) {
                this.action = this.actions.new(LABELS, this.selectedElement);
            } else {
                this.action = this.actions.new(RESHAPE, this.selectedElement);
            }
            this.action.update();
        } else if (this.click) {
            this.hoverSet.reset();
            this.hoverSet.add(
                this.elements.surrounded(
                    this.click,
                    this.view.coordinate
                )
            );
        }
    }

    moveMouse() {
        if (!this.menu.initialized) {
            let hovered = this.selection.mouseOver() || this.elements.mouseOver()
            if (!hovered) {
                this.cursor.arrow();
            }
            this.hoverSet.set(hovered);
        }
    }

    clickMouseOnCanvas() {
        this.dontDrag = false;
        if (this.dragging) {
            this.dragging = false;
            this.prevX = null;
            this.prevY = null;
            if (this.click) {
                // select all elements in the rectangle
                this.hoverSet.reset();
                this.selection.click(
                    this.elements.surrounded(this.click, this.view.snappedCoordinate)
                );
                this.click = null;
            }
            if (this.selectedElement && this.action) {
                this.action.finish();
                this.action = null;
            }
            this.selectedElement = null;
            return;
        } else if (this.menu.initialized) {
            this.menu.remove();
            return;
        }
        this.click = null;
        let selectedElement = this.selection.hovered() || this.elements.hovered();
        if (selectedElement) {
            if (this.multiSelectMode) {
                let element = selectedElement instanceof AbstractShape ? selectedElement : selectedElement.parent;
                element.selected ? this.selection.remove(element) : this.selection.add(element);
            } else {
                this.selection.click(selectedElement);
                this.cursor.grab();
                if (!this.mobile) {
                    this.sideBar.setTab('info');
                }
            }
        } else {
            if (!(this.p.keyIsDown(this.p.CONTROL) || this.p.keyIsDown(this.p.SHIFT))) {
                this.selection.reset();
            }
        }
    }

    doubleClickedOnCanvas() {
        let selectedElement = this.elements.hovered();
        if (selectedElement) {
            if (!this.p.keyIsDown(this.p.CONTROL)
                && !this.p.keyIsDown(this.p.SHIFT)
            ) {
                this.selection.reset();
            }
            this.elements.collection.map(shape => shape.type == selectedElement.type ? this.selection.add(shape) : '');
            this.cursor.grab();
            if (!this.mobile) {
                this.sideBar.setTab('info');
            }
            return true;
        } else {
            this.selection.reset();
        }
    }

    getTips() {
        if (this.touch) {
            if (this.multiSelectMode) {
                return "Tap elements to add or remove from selection.";
            }
            if (this.selection.length == 1) {
                return "Drag to move. Drag corners or borders to resize.";
            }
            if (this.selection.length > 0) {
                return "Drag to move selected elements.";
            }
            return "Tap to select. Use multi-select button to select multiple.";
        }
        if (this.click && this.dragging) {
            return "Hold CTRL to add to selection. Hold SHIFT to remove from selection."
        }
        if (this.selection.length == 1) {
            return "Drag to move. Drag corners or borders to resize. You can also move the label. Press 'D' to duplicate."
        }
        if (this.selection.length > 0) {
            return "Drag to move. Press 'D' to duplicate."
        }
        return "Click to select elements. Hold [CTRL] or [SHIFT] to add/remove from selection. Select multiple by dragging."
    }

    deactivateButton() {
        super.deactivateButton();
        this.provider.menu.hideMultiSelectButton();
        this.multiSelectMode = false;
    }

    deactivate() {
        super.deactivate();
        this.menu.remove();
        this.selection.reset();
    }

    pressKeyWithControl(code) {
        switch (code) {
            case 65: // A
                this.selection.reset();
                this.elements.collection.map(element => this.selection.add(element));
                return false;
            case 67: // C
                Clipboard.copy(this.selection.copy());
                return false;
            case 86: // V
                Clipboard.paste(this.tools, this.elementFactory, this.selection, this.speciesCollection);
                return false;
        }
        return;
    }

    pressKeyWithShift(code) {
        switch (code) {
            case 65: // A
                if (this.selection.length == 1) {
                    this.actions.execute(SEND_TO_BACK, this.selection.first);
                }
                // Handle multiple elements
                // this.selection.collection.map(element => this.elements.sendToBack(element));
                return false;
            case 66: // B
                if (this.selection.length == 1) {
                    this.actions.execute(SEND_BACKWARD, this.selection.first);
                }
                // Handle multiple elements
                // this.selection.collection.map(element => this.elements.sendBackward(element));
                return false;
            case 70: // F
                if (this.selection.length == 1) {
                    this.actions.execute(BRING_FORWARD, this.selection.first);
                }
                // Handle multiple elements
                // this.selection.collection.map(element => this.elements.bringForward(element));
                return false;
            case 75: // K
                this.drawMenu.stroke = !this.drawMenu.stroke;
                return false;
            case 79: // O
                if (this.selection.length == 1) {
                    this.actions.execute(BRING_TO_FRONT, this.selection.first);
                }
                // Handle multiple elements
                // this.selection.collection.map(element => this.elements.bringToFront(element));
                return false;
            case 88: // X
                this.drawMenu.fill = !this.drawMenu.fill;
                return false;
        }
        return;
    }

    pressKeyHotKeysDisabled(code) {
        switch (code) {
            case 27: // Esc
                if (this.menu.initialized) {
                    this.menu.remove();
                    return;
                }
                setTimeout(() => {
                    this.getTab('info').activeInput?.elt.blur();
                    this.getTab('info').activeInput = null;
                }, 10);
                return;
        }
        return;
    }

    pressKey(code) {
        if (this.selection.selected) {
            switch (code) {
                case 8: // Backspace
                    this.selection.delete();
                    return;
                case 27: // Esc
                    if (this.menu.initialized) {
                        this.menu.remove();
                        return;
                    }
                    this.selection.reset();
                    break;
                case 37: // left arrow
                    this.actions.execute(MOVE_WITH_ARROW_KEY, 'left');
                    return;
                case 38: // up arrow
                    this.actions.execute(MOVE_WITH_ARROW_KEY, 'up');
                    return;
                case 39: // right arrow
                    this.actions.execute(MOVE_WITH_ARROW_KEY, 'right');
                    return;
                case 40: // down arrow
                    this.actions.execute(MOVE_WITH_ARROW_KEY, 'down');
                    return;
                case 46: // Delete
                    this.selection.delete();
                    return;
                case 49: // 1
                    focusInput(this.getInput(0));
                    return;
                case 50: // 2
                    focusInput(this.getInput(1));
                    return;
                case 51: // 3
                    focusInput(this.getInput(2));
                    return;
                case 52: // 4
                    focusInput(this.getInput(3));
                    return;
                case 53: // 5
                    focusInput(this.getInput(4));
                    return;
                case 54: // 6
                    focusInput(this.getInput(5));
                    return;
                case 55: // 7
                    focusInput(this.getInput(6));
                    return;
                case 56: // 8
                    focusInput(this.getInput(7));
                    return;
                case 57: // 9
                    focusInput(this.getInput(8));
                    return;
                case 66: // B
                    focusInput(this.getInput('label'));
                    return;
                case 68: // D
                    this.selection.clone();
                    return;
                case 71: // G
                    focusInput(this.getInput('height'));
                    return;
                case 75: // K
                    setTimeout(() => this.drawMenu.strokePicker.elt.click(), 10);
                    return;
                case 87: // W
                    focusInput(this.getInput('width'));
                    return;
                case 88: // X
                    setTimeout(() => this.drawMenu.fillPicker.elt.click(), 10);
                    return;
                case 90: // Z
                    focusInput(this.getInput('size'));
                    return;
            }
        }

        switch (code) {
            case 37: // left arrow
                this.view.move(this.arrowMove, 0);
                return;
            case 38: // up arrow
                this.view.move(0, this.arrowMove);
                return;
            case 39: // right arrow
                this.view.move(-this.arrowMove, 0);
                return;
            case 40: // down arrow
                this.view.move(0, -this.arrowMove);
                return;
        }
    }
}
